import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { fetchFestivals, generateFestivalSummary } from "../lib/api";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const buildMonthGrid = (year, month) => {
    const first = new Date(year, month, 1);
    const startOffset = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function FestivalCalendar() {
    const [festivals, setFestivals] = useState([]);
    const [params, setParams] = useSearchParams();
    const [year, setYear] = useState(2026);
    const [month, setMonth] = useState(0);
    const [selected, setSelected] = useState(null);
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    useEffect(() => {
        fetchFestivals().then((all) => {
            setFestivals(all);
            const pid = params.get("festival");
            if (pid) {
                const f = all.find((x) => x.id === pid);
                if (f) {
                    const d = new Date(f.date + "T00:00:00");
                    setYear(d.getFullYear());
                    setMonth(d.getMonth());
                    setSelected(f);
                }
            } else if (all.length) {
                const today = new Date();
                const upcoming = all.find((f) => new Date(f.date) >= today);
                if (upcoming) {
                    const d = new Date(upcoming.date + "T00:00:00");
                    setYear(d.getFullYear());
                    setMonth(d.getMonth());
                }
            }
        });
        // eslint-disable-next-line
    }, []);

    const festivalsByDate = useMemo(() => {
        const map = {};
        for (const f of festivals) {
            if (!map[f.date]) map[f.date] = [];
            map[f.date].push(f);
        }
        return map;
    }, [festivals]);

    const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

    const shiftMonth = (dir) => {
        let m = month + dir;
        let y = year;
        if (m < 0) { m = 11; y -= 1; }
        if (m > 11) { m = 0; y += 1; }
        setMonth(m);
        setYear(y);
    };

    const openFestival = (f) => {
        setSelected(f);
        setSummary(null);
        setParams({ festival: f.id });
    };

    const closePanel = () => {
        setSelected(null);
        setSummary(null);
        const p = new URLSearchParams(params);
        p.delete("festival");
        setParams(p);
    };

    const loadSummary = async () => {
        if (!selected) return;
        setSummaryLoading(true);
        try {
            const res = await generateFestivalSummary(selected.id);
            setSummary(res.summary);
        } catch {
            setSummary("Unable to generate a summary right now. Please try again in a moment.");
        } finally {
            setSummaryLoading(false);
        }
    };

    return (
        <div data-testid="calendar-page" className="min-h-screen">
            <section className="pt-36 pb-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted mb-6">
                    The Sacred Timeline
                </p>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-tattva-ink max-w-4xl">
                    Festivals of{" "}
                    <span className="italic text-tattva-terracotta">2026</span>
                </h1>
                <p className="mt-8 max-w-2xl text-base text-tattva-muted leading-relaxed">
                    Tithi, nakshatra, and the stories beneath — click any marked
                    day to explore its significance, rituals and related films.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-8 flex items-center justify-between border-b border-tattva-ornament">
                <button
                    onClick={() => shiftMonth(-1)}
                    data-testid="calendar-prev-month"
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-tattva-ink hover:text-tattva-terracotta transition-colors py-4"
                >
                    <ChevronLeft size={16} /> Prev
                </button>
                <h2
                    data-testid="calendar-month-label"
                    className="font-serif text-3xl md:text-4xl text-tattva-ink"
                >
                    {MONTHS[month]}{" "}
                    <span className="text-tattva-muted">{year}</span>
                </h2>
                <button
                    onClick={() => shiftMonth(1)}
                    data-testid="calendar-next-month"
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-tattva-ink hover:text-tattva-terracotta transition-colors py-4"
                >
                    Next <ChevronRight size={16} />
                </button>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
                <div className="grid grid-cols-7 border-l border-t border-tattva-ornament">
                    {DAYS.map((d) => (
                        <div
                            key={d}
                            className="border-r border-b border-tattva-ornament py-3 px-3 text-[10px] uppercase tracking-[0.25em] text-tattva-muted bg-tattva-surface/40"
                        >
                            {d}
                        </div>
                    ))}
                    {grid.map((cell, i) => {
                        if (!cell) {
                            return (
                                <div
                                    key={i}
                                    className="border-r border-b border-tattva-ornament bg-tattva-surface/20 min-h-[110px] md:min-h-[130px]"
                                />
                            );
                        }
                        const dateIso = iso(cell);
                        const dayFests = festivalsByDate[dateIso] || [];
                        const hasFest = dayFests.length > 0;
                        return (
                            <button
                                key={i}
                                disabled={!hasFest}
                                onClick={() =>
                                    hasFest && openFestival(dayFests[0])
                                }
                                data-testid={`calendar-day-${dateIso}`}
                                className={`border-r border-b border-tattva-ornament text-left p-3 min-h-[110px] md:min-h-[130px] transition-colors ${
                                    hasFest
                                        ? "hover:bg-tattva-surface cursor-pointer"
                                        : "cursor-default"
                                }`}
                            >
                                <div className="flex items-start justify-between">
                                    <span
                                        className={`font-serif text-2xl ${
                                            hasFest
                                                ? "text-tattva-terracotta"
                                                : "text-tattva-ink"
                                        }`}
                                    >
                                        {cell.getDate()}
                                    </span>
                                    {hasFest && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-tattva-terracotta mt-2" />
                                    )}
                                </div>
                                {hasFest && (
                                    <div className="mt-2">
                                        <p className="font-serif text-sm text-tattva-ink leading-snug truncate">
                                            {dayFests[0].name}
                                        </p>
                                        {dayFests[0].tithi && (
                                            <p className="text-[9px] uppercase tracking-[0.15em] text-tattva-muted truncate mt-1">
                                                {dayFests[0].tithi}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </section>

            {selected && (
                <div
                    className="fixed inset-0 z-50 flex justify-end"
                    data-testid="festival-detail-sheet"
                >
                    <div
                        className="absolute inset-0 bg-tattva-ink/40 backdrop-blur-sm"
                        onClick={closePanel}
                    />
                    <aside className="relative w-full max-w-xl bg-tattva-bg h-full overflow-y-auto shadow-2xl animate-fade-in">
                        <button
                            onClick={closePanel}
                            data-testid="festival-detail-close"
                            className="absolute top-5 right-5 text-tattva-ink hover:text-tattva-terracotta"
                            aria-label="Close"
                        >
                            <X size={22} />
                        </button>
                        <div className="p-8 md:p-12">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted mb-3">
                                {new Date(
                                    selected.date + "T00:00:00",
                                ).toLocaleDateString("en-US", {
                                    weekday: "long",
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                })}
                            </p>
                            <h2 className="font-serif text-4xl md:text-5xl text-tattva-ink leading-tight">
                                {selected.name}
                            </h2>
                            {selected.sanskrit_name && (
                                <p className="font-display text-3xl text-tattva-terracotta mt-2">
                                    {selected.sanskrit_name}
                                </p>
                            )}
                            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-tattva-ornament pt-6">
                                {[
                                    ["Tithi", selected.tithi],
                                    ["Nakshatra", selected.nakshatra],
                                    ["Paksha", selected.paksha],
                                    ["Month", selected.month_hindu],
                                ].map(([label, val]) =>
                                    val ? (
                                        <div key={label}>
                                            <p className="text-[10px] uppercase tracking-[0.25em] text-tattva-muted mb-1">
                                                {label}
                                            </p>
                                            <p className="font-serif text-lg text-tattva-ink">
                                                {val}
                                            </p>
                                        </div>
                                    ) : null,
                                )}
                            </div>
                            <div className="mt-10">
                                <p className="text-[11px] uppercase tracking-[0.25em] text-tattva-muted mb-4">
                                    Significance
                                </p>
                                <p className="text-base leading-relaxed text-tattva-ink">
                                    {selected.significance}
                                </p>
                            </div>
                            <div className="mt-10 pt-8 border-t border-tattva-ornament">
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-[11px] uppercase tracking-[0.25em] text-tattva-muted">
                                        Deeper Reading
                                    </p>
                                    {!summary && (
                                        <button
                                            onClick={loadSummary}
                                            disabled={summaryLoading}
                                            data-testid="generate-summary-btn"
                                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-tattva-terracotta hover:text-tattva-terracotta-dark transition-colors disabled:opacity-60"
                                        >
                                            <Sparkles size={14} />
                                            {summaryLoading
                                                ? "Weaving…"
                                                : "Unfold essay"}
                                        </button>
                                    )}
                                </div>
                                {summaryLoading && (
                                    <div className="space-y-2">
                                        <div className="h-3 bg-tattva-surface animate-pulse" />
                                        <div className="h-3 bg-tattva-surface animate-pulse w-11/12" />
                                        <div className="h-3 bg-tattva-surface animate-pulse w-4/5" />
                                    </div>
                                )}
                                {summary && (
                                    <div
                                        data-testid="festival-summary"
                                        className="font-serif text-lg leading-[1.7] text-tattva-ink whitespace-pre-line"
                                    >
                                        {summary}
                                    </div>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
